import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { COURSE_API_URL, CONTACT_API_URL } from './lib/api-config';
import BookingTable from './components/bookings/BookingTable';
import EditBookingModal from './components/bookings/EditBookingModal';
import CreateBookingModal from './components/bookings/CreateBookingModal';
import { InvoiceModal, SmsModal } from './components/bookings/BookingModals';
import LoadingSpinner from './components/ui/LoadingSpinner';
import AlertMessage from './components/ui/AlertMessage';
import './Bookings.css';

const api = axios.create({ withCredentials: true });

function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('confirmed');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [showSmsModal, setShowSmsModal] = useState(false);
  const [smsMessage, setSmsMessage] = useState('');
  const [smsSending, setSmsSending] = useState(false);
  const [smsTarget, setSmsTarget] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [editForm, setEditForm] = useState({
    participantFirstName: '', participantLastName: '', participantEmail: '',
    participantPhone: '', company: '', organizationNumber: '', billingAddress: '',
    invoiceReference: '', costCenter: '', specialDiet: '', status: 'Confirmed',
    invoiceStatus: 'NotInvoiced', invoiceNumber: '', invoiceDate: '',
    attended: '', cancellationReason: '', notes: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const emptyParticipant = { firstName: '', lastName: '', email: '', phone: '', specialDiet: '' };
  const [createForm, setCreateForm] = useState({
    courseInstanceId: '', participants: [{ ...emptyParticipant }],
    company: '', organizationNumber: '', billingAddress: '', invoiceReference: '',
    costCenter: '', notes: '', price: ''
  });
  const [sendConfirmation, setSendConfirmation] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => { fetchInstances(); }, []);

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInstance, activeTab, currentPage]);

  const fetchInstances = async () => {
    try {
      const res = await api.get(`${COURSE_API_URL}/instances`);
      const all = res.data.data || [];
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const upcoming = all
        .filter(i => i.startDate && new Date(i.startDate) >= today)
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
      const past = all
        .filter(i => !i.startDate || new Date(i.startDate) < today)
        .sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0));
      setInstances([...upcoming, ...past]);
    } catch (error) {
      console.error('Fel vid hämtning av kurstillfällen:', error);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('page', currentPage);
      params.append('pageSize', pageSize);
      if (selectedInstance !== 'all') params.append('instanceId', selectedInstance);
      if (activeTab !== 'all') {
        const statusMap = { 'confirmed': 'Confirmed', 'waitlist': 'Waitlist', 'cancelled': 'Cancelled' };
        if (statusMap[activeTab]) params.append('status', statusMap[activeTab]);
      }
      const res = await api.get(`${COURSE_API_URL}/bookings?${params.toString()}`);
      setBookings(res.data.data);
      if (res.data.pagination) {
        setTotalPages(res.data.pagination.totalPages);
        setTotalCount(res.data.pagination.total);
      }
      setLoading(false);
    } catch (error) {
      console.error('Fel vid hämtning:', error);
      setLoading(false);
    }
  };

  const handleInstanceChange = (e) => {
    setSelectedInstance(e.target.value);
    setCurrentPage(1);
    setSelectedBookings([]);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setSelectedBookings([]);
  };

  const handleCheckboxChange = (bookingId) => {
    setSelectedBookings(prev =>
      prev.includes(bookingId) ? prev.filter(id => id !== bookingId) : [...prev, bookingId]
    );
  };

  const handleSelectAll = () => {
    if (selectedBookings.length === bookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(bookings.map(b => b.id));
    }
  };

  const createContact = async (booking) => {
    try {
      const searchRes = await api.get(`${CONTACT_API_URL}/contacts/search/${booking.participantEmail}`);
      if (searchRes.data.data.length > 0) {
        setError('Kontakt finns redan i systemet');
        setTimeout(() => setError(''), 3000);
        return;
      }
      await api.post(`${CONTACT_API_URL}/contacts`, {
        firstName: booking.participantFirstName,
        lastName: booking.participantLastName,
        email: booking.participantEmail,
        phone: booking.participantPhone || '',
        company: booking.company || '',
        source: 'Kursbokning',
        notes: `Bokade ${booking.courseInstance.course.name} - ${booking.courseInstance.startDate}`
      });
      setSuccess('Kontakt skapad i CRM');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Kunde inte skapa kontakt: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  const offerWaitlistSpot = async (booking) => {
    if (window.confirm(`Erbjud plats till ${booking.participantFirstName} ${booking.participantLastName}?`)) {
      try {
        setSuccess(`E-post skickat till ${booking.participantEmail} med erbjudande om plats`);
        setTimeout(() => setSuccess(''), 5000);
        booking.waitlistOfferedAt = new Date();
        setBookings([...bookings]);
      } catch (error) {
        setError('Kunde inte erbjuda plats: ' + (error.response?.data?.error || error.message));
        setTimeout(() => setError(''), 5000);
      }
    }
  };

  const generateInvoiceData = () => {
    const selected = bookings.filter(b => selectedBookings.includes(b.id));
    if (selected.length === 0) return;
    setShowInvoiceModal(true);
  };

  const exportToCSV = () => {
    const selected = bookings.filter(b => selectedBookings.includes(b.id));
    const headers = ['Företag', 'Namn', 'E-post', 'Telefon', 'Kurs', 'Datum', 'Pris', 'Status'];
    const rows = selected.map(b => [
      b.company, `${b.participantFirstName} ${b.participantLastName}`,
      b.participantEmail, b.participantPhone || '-',
      b.courseInstance.course.name, b.courseInstance.startDate, b.price, b.invoiceStatus
    ]);
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `fakturaunderlag_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportToExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedInstance !== 'all') params.append('instanceId', selectedInstance);
      if (activeTab === 'confirmed') params.append('status', 'Confirmed');
      else if (activeTab === 'waitlist') params.append('status', 'Waitlist');
      else if (activeTab === 'cancelled') params.append('status', 'Cancelled');
      const response = await api.get(`${COURSE_API_URL}/reports/bookings/excel?${params}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `bokningar_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
    } catch (err) {
      setError('Kunde inte exportera till Excel');
    }
  };

  const exportRevenueReport = async () => {
    try {
      const year = new Date().getFullYear();
      const response = await api.get(`${COURSE_API_URL}/reports/revenue/excel?year=${year}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `intaktsrapport_${year}.xlsx`;
      link.click();
    } catch (err) {
      setError('Kunde inte exportera intäktsrapport');
    }
  };

  const updateAttendance = async (bookingId, attendedValue) => {
    try {
      const attended = attendedValue === 'true' ? true : attendedValue === 'false' ? false : null;
      await api.put(`${COURSE_API_URL}/bookings/${bookingId}`, { attended });
      const booking = bookings.find(b => b.id === bookingId);
      booking.attended = attended;
      setBookings([...bookings]);
      setSuccess('Närvaro uppdaterad');
      setTimeout(() => setSuccess(''), 2000);
    } catch (error) {
      setError('Kunde inte uppdatera närvaro: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  const sendInvoiceEmail = async (booking) => {
    if (booking.invoiceEmailSentAt) {
      if (!window.confirm('Fakturamejl har redan skickats. Vill du skicka igen?')) return;
    }
    try {
      const res = await api.post(`${COURSE_API_URL}/bookings/${booking.id}/send-invoice-email`);
      if (res.data.success) {
        booking.invoiceEmailSentAt = new Date().toISOString();
        setBookings([...bookings]);
        setSuccess('Fakturamejl skickat till lyssnare@projektledarpodden.se');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      setError('Kunde inte skicka fakturamejl: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  const sendBulkInvoiceEmails = async () => {
    if (selectedInstance === 'all') {
      setError('Välj ett kurstillfälle först');
      setTimeout(() => setError(''), 3000);
      return;
    }
    if (!window.confirm('Skicka fakturamejl för alla bokningar som inte redan fått mejl?')) return;
    try {
      const res = await api.post(`${COURSE_API_URL}/instances/${selectedInstance}/send-invoice-emails`);
      if (res.data.success) {
        setSuccess(`${res.data.sent} fakturamejl skickade`);
        setTimeout(() => setSuccess(''), 3000);
        fetchBookings();
      }
    } catch (error) {
      setError('Kunde inte skicka fakturamejl: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleEditBooking = (booking) => {
    setEditForm({
      participantFirstName: booking.participantFirstName || '',
      participantLastName: booking.participantLastName || '',
      participantEmail: booking.participantEmail || '',
      participantPhone: booking.participantPhone || '',
      company: booking.company || '',
      organizationNumber: booking.organizationNumber || '',
      billingAddress: booking.billingAddress || '',
      invoiceReference: booking.invoiceReference || '',
      costCenter: booking.costCenter || '',
      specialDiet: booking.specialDiet || '',
      price: booking.price || '',
      status: booking.status || 'Confirmed',
      invoiceStatus: booking.invoiceStatus || 'NotInvoiced',
      invoiceNumber: booking.invoiceNumber || '',
      invoiceDate: booking.invoiceDate?.split('T')[0] || '',
      attended: booking.attended === true ? 'true' : booking.attended === false ? 'false' : '',
      cancellationReason: booking.cancellationReason || '',
      notes: booking.notes || ''
    });
    setEditingBooking(booking);
    setShowEditModal(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = {
        ...editForm,
        price: editForm.price !== '' ? parseFloat(editForm.price) : null,
        attended: editForm.attended === 'true' ? true : editForm.attended === 'false' ? false : null,
        invoiceDate: editForm.invoiceDate || null
      };
      await api.put(`${COURSE_API_URL}/bookings/${editingBooking.id}`, payload);
      setSuccess('Bokning uppdaterad');
      setShowEditModal(false);
      fetchBookings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Kunde inte spara bokning');
    }
  };

  const handleRebook = async (payload) => {
    setError('');
    const res = await api.post(`${COURSE_API_URL}/bookings/${editingBooking.id}/rebook`, payload);
    const targetCourse = res.data.data?.courseInstance?.course?.name || 'ny kurs';
    const targetDate = res.data.data?.courseInstance?.startDate?.slice(0, 10) || '';
    const emailNote = res.data.emailSent ? ' Bekräftelse-mejl skickat.' : ' (mejl misslyckades — kolla loggar)';
    setSuccess(`Bokning ombokad till ${targetCourse} ${targetDate}.${emailNote}`);
    setShowEditModal(false);
    fetchBookings();
    setTimeout(() => setSuccess(''), 5000);
    return { handled: true };
  };

  const handleOpenCreateModal = () => {
    const initialForm = {
      courseInstanceId: selectedInstance !== 'all' ? selectedInstance : '',
      participants: [{ ...emptyParticipant }],
      company: '', organizationNumber: '', billingAddress: '', invoiceReference: '',
      costCenter: '', notes: '', price: ''
    };
    if (selectedInstance !== 'all') {
      const inst = instances.find(i => i.id === parseInt(selectedInstance));
      if (inst) initialForm.price = inst.price || inst.course?.price || '';
    }
    setCreateForm(initialForm);
    setSendConfirmation(false);
    setCreateError('');
    setShowCreateModal(true);
  };

  const updateParticipant = (index, field, value) => {
    setCreateForm(prev => {
      const participants = [...prev.participants];
      participants[index] = { ...participants[index], [field]: value };
      return { ...prev, participants };
    });
  };

  const addParticipant = () => {
    setCreateForm(prev => ({ ...prev, participants: [...prev.participants, { ...emptyParticipant }] }));
  };

  const removeParticipant = (index) => {
    if (createForm.participants.length <= 1) return;
    setCreateForm(prev => ({ ...prev, participants: prev.participants.filter((_, i) => i !== index) }));
  };

  const handleCreateInstanceChange = (instanceId) => {
    const inst = instances.find(i => i.id === parseInt(instanceId));
    setCreateForm(prev => ({
      ...prev, courseInstanceId: instanceId,
      price: inst ? (inst.price || inst.course?.price || '') : ''
    }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreateLoading(true);
    try {
      const { participants, courseInstanceId, company, organizationNumber,
              billingAddress, invoiceReference, costCenter, notes, price } = createForm;
      const sharedData = {
        courseInstanceId: parseInt(courseInstanceId),
        company, organizationNumber, billingAddress, invoiceReference,
        costCenter, notes,
        price: price !== '' ? parseFloat(price) : undefined,
        sendConfirmationEmail: sendConfirmation
      };
      const results = [];
      const errors = [];
      for (let i = 0; i < participants.length; i++) {
        const p = participants[i];
        try {
          const response = await api.post(`${COURSE_API_URL}/bookings`, {
            ...sharedData,
            participantFirstName: p.firstName, participantLastName: p.lastName,
            participantEmail: p.email,
            participantPhone: p.phone || undefined,
            specialDiet: p.specialDiet || undefined
          });
          results.push(response.data);
        } catch (err) {
          errors.push(`${p.firstName} ${p.lastName}: ${err.response?.data?.error || err.message}`);
        }
      }
      if (errors.length > 0 && results.length > 0) {
        setSuccess(`${results.length} av ${participants.length} bokningar skapade`);
        setCreateError(`Misslyckades: ${errors.join(', ')}`);
      } else if (errors.length > 0) {
        setCreateError(errors.join(', '));
      } else {
        setSuccess(participants.length > 1
          ? `${participants.length} bokningar skapade`
          : results[0]?.message || 'Bokning skapad');
        setShowCreateModal(false);
      }
      fetchBookings();
      setTimeout(() => setSuccess(''), 4000);
    } catch (error) {
      setCreateError(error.response?.data?.error || 'Kunde inte skapa bokning');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCancelBooking = async (booking) => {
    const name = `${booking.participantFirstName} ${booking.participantLastName}`;
    const reason = window.prompt(`Ange anledning till avbokning för "${name}":`);
    if (reason === null) return;
    try {
      await api.put(`${COURSE_API_URL}/bookings/${booking.id}/cancel`, { cancellationReason: reason });
      setSuccess('Bokning avbokad');
      fetchBookings();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Kunde inte avboka: ' + (error.response?.data?.error || error.message));
      setTimeout(() => setError(''), 5000);
    }
  };

  const openSmsModal = (target) => {
    setSmsTarget(target);
    setSmsMessage('');
    setShowSmsModal(true);
  };

  const sendSms = async () => {
    if (!smsMessage.trim()) { setError('Skriv ett meddelande först'); return; }
    if (smsMessage.length > 160) { setError('Meddelandet får max vara 160 tecken'); return; }
    setSmsSending(true);
    setError('');
    try {
      let result;
      if (smsTarget === 'instance' && selectedInstance !== 'all') {
        result = await api.post(`${COURSE_API_URL}/sms/send-to-instance/${selectedInstance}`, {
          message: smsMessage, includeWaitlist: false
        });
      } else if (smsTarget === 'selected') {
        const results = [];
        for (const bookingId of selectedBookings) {
          try {
            const res = await api.post(`${COURSE_API_URL}/sms/send-to-booking/${bookingId}`, { message: smsMessage });
            results.push(res.data);
          } catch (err) {
            results.push({ success: false, error: err.response?.data?.error || err.message });
          }
        }
        result = { data: { data: { sent: results.filter(r => r.success).length, failed: results.filter(r => !r.success).length } } };
      } else if (typeof smsTarget === 'object' && smsTarget?.id) {
        result = await api.post(`${COURSE_API_URL}/sms/send-to-booking/${smsTarget.id}`, { message: smsMessage });
      }
      const data = result.data.data;
      if (data.sent !== undefined) {
        setSuccess(`SMS skickat! ${data.sent} lyckade${data.failed > 0 ? `, ${data.failed} misslyckades` : ''}${data.skipped > 0 ? `, ${data.skipped} hoppades över` : ''}`);
      } else {
        setSuccess(`SMS skickat till ${data.participant || 'mottagaren'}`);
      }
      setShowSmsModal(false);
      setSmsMessage('');
      setTimeout(() => setSuccess(''), 5000);
    } catch (error) {
      setError('Kunde inte skicka SMS: ' + (error.response?.data?.error || error.message));
    } finally {
      setSmsSending(false);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('sv-SE');

  const formatPrice = (price) => new Intl.NumberFormat('sv-SE', {
    style: 'currency', currency: 'SEK', minimumFractionDigits: 0
  }).format(price);

  if (loading) {
    return <LoadingSpinner message="⏳ Laddar bokningar..." />;
  }

  return (
    <div className="bookings-container">
      <AlertMessage error={error} success={success} />

      <header className="bookings-header">
        <h1>Bokningshantering</h1>
        <div className="header-stats">
          <div className="stat">
            <span className="stat-value">{totalCount}</span>
            <span className="stat-label">Totalt (filtrerat)</span>
          </div>
          <div className="stat">
            <span className="stat-value">{bookings.length}</span>
            <span className="stat-label">På sidan</span>
          </div>
          <div className="stat">
            <span className="stat-value">
              {formatPrice(bookings.filter(b => b.status === 'Confirmed').reduce((sum, b) => sum + parseFloat(b.price), 0))}
            </span>
            <span className="stat-label">Intäkt (sidan)</span>
          </div>
        </div>
      </header>

      <div className="bookings-controls">
        <select value={selectedInstance} onChange={handleInstanceChange} className="instance-filter">
          <option value="all">Alla kurser</option>
          {instances.map(instance => (
            <option key={instance.id} value={instance.id}>
              {instance.course.name} - {formatDate(instance.startDate)} ({instance.location})
            </option>
          ))}
        </select>
        <button className="btn btn-success" onClick={handleOpenCreateModal}>+ Ny bokning</button>

        <div className="tabs">
          {['confirmed', 'waitlist', 'cancelled', 'all'].map(tab => (
            <button key={tab} className={`tab ${activeTab === tab ? 'active' : ''}`} onClick={() => handleTabChange(tab)}>
              {tab === 'confirmed' ? 'Bekräftade' : tab === 'waitlist' ? 'Väntelista' : tab === 'cancelled' ? 'Avbokade' : 'Alla'}
            </button>
          ))}
        </div>
      </div>

      {selectedBookings.length > 0 && (
        <div className="bulk-actions">
          <span>{selectedBookings.length} bokningar valda</span>
          <button className="btn btn-primary" onClick={generateInvoiceData}>📄 Fakturaunderlag</button>
          <button className="btn btn-secondary" onClick={exportToCSV}>📥 Exportera CSV</button>
          <button className="btn btn-sms" onClick={() => openSmsModal('selected')}>📱 Skicka SMS</button>
        </div>
      )}

      {selectedInstance !== 'all' && (
        <div className="instance-actions">
          <button className="btn btn-sms" onClick={() => openSmsModal('instance')}>📱 Skicka SMS till alla deltagare</button>
          <button className="btn btn-secondary" onClick={sendBulkInvoiceEmails}>🧾 Skicka fakturamejl för kurstillfället</button>
        </div>
      )}

      <div className="export-actions">
        <button className="btn btn-export" onClick={exportToExcel}>📊 Exportera bokningar (Excel)</button>
        <button className="btn btn-export" onClick={exportRevenueReport}>📈 Intäktsrapport {new Date().getFullYear()} (Excel)</button>
      </div>

      <BookingTable
        bookings={bookings}
        selectedBookings={selectedBookings}
        formatDate={formatDate}
        formatPrice={formatPrice}
        onCheckboxChange={handleCheckboxChange}
        onSelectAll={handleSelectAll}
        onEditBooking={handleEditBooking}
        onCreateContact={createContact}
        onSendInvoiceEmail={sendInvoiceEmail}
        onOfferWaitlistSpot={offerWaitlistSpot}
        onCancelBooking={handleCancelBooking}
        onUpdateAttendance={updateAttendance}
        currentPage={currentPage}
        totalPages={totalPages}
        totalCount={totalCount}
        onPageChange={setCurrentPage}
      />

      <InvoiceModal
        show={showInvoiceModal}
        bookings={bookings}
        selectedBookings={selectedBookings}
        formatPrice={formatPrice}
        onExportCSV={exportToCSV}
        onClose={() => setShowInvoiceModal(false)}
      />

      <EditBookingModal
        show={showEditModal}
        booking={editingBooking}
        form={editForm}
        setForm={setEditForm}
        formatDate={formatDate}
        instances={instances}
        onSubmit={handleEditSubmit}
        onRebook={handleRebook}
        onClose={() => setShowEditModal(false)}
      />

      <CreateBookingModal
        show={showCreateModal}
        form={createForm}
        setForm={setCreateForm}
        instances={instances}
        formatDate={formatDate}
        sendConfirmation={sendConfirmation}
        setSendConfirmation={setSendConfirmation}
        error={createError}
        loading={createLoading}
        onSubmit={handleCreateSubmit}
        onClose={() => setShowCreateModal(false)}
        onInstanceChange={handleCreateInstanceChange}
        onAddParticipant={addParticipant}
        onRemoveParticipant={removeParticipant}
        onUpdateParticipant={updateParticipant}
      />

      <SmsModal
        show={showSmsModal}
        message={smsMessage}
        setMessage={setSmsMessage}
        sending={smsSending}
        smsTarget={smsTarget}
        selectedBookings={selectedBookings}
        instances={instances}
        selectedInstance={selectedInstance}
        onSend={sendSms}
        onClose={() => setShowSmsModal(false)}
      />
    </div>
  );
}

export default Bookings;
